import { useMemo, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAuth, type User } from '../lib/auth'
import { humanError } from '../lib/errors'

type Mode = 'login' | 'register' | 'join'

export function AuthPage() {
  const { user, setUser, loading } = useAuth()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const initial = (params.get('mode') as Mode) || 'login'
  const [mode, setMode] = useState<Mode>(initial === 'join' ? 'join' : initial)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)

  const title = useMemo(() => {
    if (mode === 'login') return 'Вход'
    if (mode === 'join') return 'Присоединиться к компании'
    return 'Создать компанию'
  }, [mode])

  if (loading) {
    return (
      <div className="app-shell">
        <div className="loading-block" role="status">
          <span className="spinner" aria-hidden="true" />
          Загрузка…
        </div>
      </div>
    )
  }

  if (user) {
    const next = params.get('next')
    const safe = next && next.startsWith('/') && !next.startsWith('//') ? next : '/app'
    return <Navigate to={safe} replace />
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError('')
    setPending(true)
    const form = new FormData(e.currentTarget)
    try {
      if (mode === 'login') {
        const data = await api<{ user: User }>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            email: String(form.get('email')),
            password: String(form.get('password')),
          }),
        })
        setUser(data.user)
      } else {
        const data = await api<{ user: User }>('/api/auth/register', {
          method: 'POST',
          body: JSON.stringify({
            email: String(form.get('email')),
            password: String(form.get('password')),
            name: String(form.get('name')),
            mode: mode === 'join' ? 'join_company' : 'create_company',
            companyName: mode === 'register' ? String(form.get('companyName')) : undefined,
            inviteCode: mode === 'join' ? String(form.get('inviteCode')) : undefined,
          }),
        })
        setUser(data.user)
      }
      const next = params.get('next')
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/app')
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Ошибка')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/" className="brand">
          <div className="brand-mark">
            W<span>ish</span>
          </div>
        </Link>
      </header>

      <div className="grid-2">
        <section className="panel stack" aria-labelledby="auth-title">
          <h1 className="section-title" id="auth-title">
            {title}
          </h1>
          <p className="section-sub">Без лишних шагов: почта, имя и компания.</p>
          <div className="tabs" role="tablist" aria-label="Режим регистрации">
            <button
              className={`tab ${mode === 'login' ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              onClick={() => setMode('login')}
            >
              Вход
            </button>
            <button
              className={`tab ${mode === 'register' ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={mode === 'register'}
              onClick={() => setMode('register')}
            >
              Новая компания
            </button>
            <button
              className={`tab ${mode === 'join' ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={mode === 'join'}
              onClick={() => setMode('join')}
            >
              Invite-код
            </button>
          </div>

          <form className="stack" onSubmit={onSubmit}>
            {mode !== 'login' && (
              <label>
                Имя
                <input name="name" required minLength={1} placeholder="Аня" autoComplete="name" />
              </label>
            )}
            <label>
              Email
              <input
                name="email"
                type="email"
                required
                placeholder="you@company.ru"
                autoComplete="email"
              />
            </label>
            <label>
              Пароль
              <input
                name="password"
                type="password"
                required
                minLength={6}
                placeholder="минимум 6 символов"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </label>
            {mode === 'register' && (
              <label>
                Название компании
                <input name="companyName" required placeholder="Acme" autoComplete="organization" />
              </label>
            )}
            {mode === 'join' && (
              <label>
                Invite-код
                <input
                  name="inviteCode"
                  required
                  placeholder="A1B2C3D4"
                  autoComplete="off"
                  style={{ textTransform: 'uppercase' }}
                />
              </label>
            )}
            {error && (
              <div className="alert alert-error" role="alert">
                {error}
              </div>
            )}
            <button className="btn btn-primary" disabled={pending} type="submit">
              {pending ? (
                <span className="row" style={{ gap: '0.5rem' }}>
                  <span className="spinner" aria-hidden="true" style={{ width: '1rem', height: '1rem' }} />
                  Секунду…
                </span>
              ) : (
                'Продолжить'
              )}
            </button>
          </form>
        </section>

        <aside className="panel stack auth-aside">
          <h2 className="section-title">Как это работает</h2>
          <ol className="stack stack-tight muted" style={{ margin: 0, paddingLeft: '1.1rem' }}>
            <li>Создаёте компанию или входите по коду коллеги.</li>
            <li>Собираете личные списки и при необходимости делитесь ими.</li>
            <li>В сезон подарков бронируете айтемы и скидываетесь.</li>
          </ol>
        </aside>
      </div>
    </div>
  )
}
